import { useState, type ReactNode } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { ColourCell, TextureMap, TextureValues, spin } from './MaterialsTable';
import { materialText } from './variantLabel';
import { useHardwareRun } from './defaultTexturesRun';
import type { HardwareMaterial } from './materialsRun';
import {
  setStandardMaterials, useStandardMaterials, type StandardMaterial,
} from './standardMaterials';
import type { Rgb } from './sampleColour';
import { useLanguage } from '../../contexts/LanguageContext';

// THE DEFAULT MATERIALS: the ones the listing never names, because they are not what the
// product is made of — the zinc-plated steel of its screws, the raw wood of its dowels, the felt
// under its feet. See standardMaterials for why there are any at all.
//
// They were a constant nobody could see, and they reached two places without saying so: the end
// of every part's Material menu, and the materials prompt, numbered after the listing's own. So
// here they are, per manual, editable — a notice with black hardware says so on its own list.
//
// ROWS OF THE ONE MATERIALS TABLE (see MaterialsTable), after the photographed ones: their own
// table once, with the same columns under different words. Editable where the others are read.
//
// RENAMING ONE IS NOT FREE, and the row does not pretend otherwise: a part's material is saved
// as its words, so a part that was "Felt / beige" keeps those words after the row is renamed and
// no longer matches anything on this list. It is ticked again in the Parts column.

// WORDS THAT ARE EDITABLE WITHOUT LOOKING LIKE A FORM: the row reads as the listing's rows do,
// and a hover or a focus is what shows it can be typed in.
// ONE FIELD FOR THE TWO LINES, lit when hovered or typed in: they are one material's name.
const nameField = 'block w-full min-w-[8rem] resize-none overflow-hidden rounded bg-transparent px-1 py-0.5 text-center font-semibold leading-snug text-slate-900 outline-none transition-colors hover:bg-slate-100 focus:bg-slate-100 focus:ring-1 focus:ring-blue-400';
const smallBtn = 'inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:border-blue-500 hover:text-blue-600';

// ONE DEFAULT MATERIAL AS A ROW, in the columns the photographed ones use: what it is, its colour,
// the parts wearing it, its surface and texture values, the map drawn from them —
// and its bin, at the end, where the others have nothing.
export function DefaultMaterialRow({ docId, at, cell, parts, proposed }: {
  docId: string;
  at: number;
  cell: string;
  // The PARTS cell, drawn by the table, which holds the parts.
  parts: ReactNode;
  // WHAT A READING NOT APPLIED SAYS OF IT — its surface and texture, shown in place of the ones
  // on the list until that reading is applied. Undefined on the applied one.
  proposed?: HardwareMaterial;
}) {
  const { t } = useLanguage();
  // WHILE THE HARDWARE CALL IS OUT, the cells it will fill spin.
  const asking = useHardwareRun(docId).busy;
  const list = useStandardMaterials(docId);
  const m = list[at];
  if (!m) return null;
  const said = proposed ?? m;
  const save = (next: StandardMaterial[]) => setStandardMaterials(docId, next);
  const change = (patch: Partial<StandardMaterial['pair']> | { rgb: Rgb }) => save(list.map((one, i) => (i !== at ? one
    : 'rgb' in patch ? { ...one, rgb: patch.rgb } : { ...one, pair: { ...one.pair, ...patch } })));
  return (
    <tr>
      <td className={cell}>
        <NameField pair={m.pair} onChange={change} />
      </td>
      {/* ASKED TOO: the call's colour, shown as it is proposed and on the list once applied. */}
      <td className={cell}>
        {asking ? spin : proposed?.colour
          ? <ColourCell rgb={proposed.colour} />
          : <ColourCell rgb={m.rgb} onChange={(rgb) => change({ rgb })} />}
      </td>
      {/* THE PARTS TOO: the same call says which piece of hardware each material is. */}
      <td className={cell}>{asking ? spin : parts}</td>
      {/* ITS SURFACE AND TEXTURE, as the photographed materials show theirs — asked with its
          textures (the Hardware call) rather than measured, since nobody photographs a screw;
          blank until then. */}
      <td className={cell}>{asking ? spin : <TextureValues surface={said} read={said.texture} />}</td>
      <td className={asking ? cell : `${cell} align-top`}>
        {asking ? spin : <TextureMap name={materialText(m.pair)} pattern={said.texture?.pattern} read={said.texture} />}
      </td>
      <td className={cell}>
        <button
          type="button"
          onClick={() => save(list.filter((_, i) => i !== at))}
          title={t('notice.defaultMaterials.remove')}
          aria-label={t('notice.defaultMaterials.remove')}
          className="rounded p-1 text-slate-400 transition-colors hover:text-rose-500"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </td>
    </tr>
  );
}

// THE NAME AS TEXT: the material on the first line, its colour on the second — and no second line
// at all where there is no colour, so a line can be deleted like any other. One text area rather
// than two inputs, which drew an empty second box under every one-word material.
//
// THE TEXT BEING TYPED IS KEPT HERE while the field has the focus: "Soft wood" and a line break
// is a colour of nothing, which the list stores as no colour — read back from it, the break
// would vanish under the cursor before the colour could be typed.
function NameField({ pair, onChange }: {
  pair: StandardMaterial['pair'];
  onChange: (patch: Partial<StandardMaterial['pair']>) => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const text = draft ?? (pair.color ? `${pair.material}\n${pair.color}` : pair.material);
  return (
    <textarea
      className={nameField}
      rows={text.split('\n').length}
      value={text}
      onFocus={() => setDraft(text)}
      onBlur={() => setDraft(null)}
      onChange={(e) => {
        const next = e.target.value;
        setDraft(next);
        const cut = next.indexOf('\n');
        onChange(cut < 0
          ? { material: next, color: '' }
          : { material: next.slice(0, cut), color: next.slice(cut + 1).replace(/\n/g, ' ') });
      }}
    />
  );
}

// Add one — under the table.
export function DefaultMaterialsButtons({ docId }: { docId: string }) {
  const { t } = useLanguage();
  const list = useStandardMaterials(docId);
  const save = (next: StandardMaterial[]) => setStandardMaterials(docId, next);
  return (
    <div className="flex flex-wrap justify-center gap-2">
      <button
        type="button"
        onClick={() => save([...list, { pair: { material: '', color: '' }, rgb: [200, 200, 200] }])}
        className={smallBtn}
      >
        <Plus className="h-4 w-4" />
        {t('notice.defaultMaterials.add')}
      </button>
    </div>
  );
}
